import { clsx } from 'clsx';
import React from 'react';
import { Checkbox } from '../../../components/checkbox';
import { Input } from '../../../components/input';

export type PickerItem = { id: string; label: string; detail?: string };

type LinkPickerListProps = {
  items: PickerItem[];
  picked: Set<string>;
  onToggle: (id: string) => void;
  onSetMany: (ids: string[], on: boolean) => void;
  isLoading?: boolean;
  emptyText: string;
  /** Omit to render without a search box (the item set is already small). */
  search?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  disabled?: boolean;
  testId: string;
};

/** Searchable checklist used by the link dialog's facility/contact pickers. */
export const LinkPickerList: React.FC<LinkPickerListProps> = ({
  items,
  picked,
  onToggle,
  onSetMany,
  isLoading = false,
  emptyText,
  search,
  onSearchChange,
  searchPlaceholder,
  disabled = false,
  testId,
}) => {
  const shownIds = items.map((item) => item.id);
  const allShownPicked = shownIds.length > 0 && shownIds.every((id) => picked.has(id));
  const isEmpty = !isLoading && items.length === 0;

  const renderItems = () => {
    if (isLoading) return <div className="tw:p-3 tw:text-sm tw:text-zinc-500">Loading...</div>;
    if (items.length === 0) {
      return (
        <div
          className="tw:p-4 tw:text-center tw:text-sm tw:italic tw:text-zinc-500"
          data-testid={`${testId}-empty`}
        >
          {emptyText}
        </div>
      );
    }
    return (
      <ul className="tw:!m-0 tw:!pl-0 tw:list-none tw:divide-y tw:divide-zinc-100">
        {items.map((item) => (
          <li key={item.id}>
            <label
              className="tw:!flex tw:!mb-0 tw:cursor-pointer tw:items-center tw:gap-3 tw:px-3 tw:py-2 tw:hover:bg-brand-green-3"
              htmlFor={`${testId}-${item.id}`}
            >
              <Checkbox
                id={`${testId}-${item.id}`}
                color="brandgreen"
                checked={picked.has(item.id)}
                onChange={() => onToggle(item.id)}
                disabled={disabled}
                data-testid={`${testId}-${item.id}`}
              />
              <span className="tw:min-w-0">
                <span className="tw:block tw:truncate tw:text-sm tw:text-zinc-800">
                  {item.label}
                </span>
                {item.detail && (
                  <span className="tw:block tw:break-words tw:text-xs tw:text-zinc-500">
                    {item.detail}
                  </span>
                )}
              </span>
            </label>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div className="tw:space-y-2" data-testid={testId}>
      {onSearchChange && (
        <Input
          type="search"
          value={search ?? ''}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={searchPlaceholder}
          disabled={disabled}
          data-testid={`${testId}-search`}
        />
      )}
      <div className="tw:flex tw:items-center tw:justify-between tw:text-xs tw:text-zinc-500">
        {items.length > 0 ? (
          <button
            type="button"
            className="tw:text-brand-green-0 tw:hover:underline tw:disabled:opacity-50"
            onClick={() => onSetMany(shownIds, !allShownPicked)}
            disabled={disabled}
            data-testid={`${testId}-toggle-all`}
          >
            {allShownPicked ? 'Deselect All' : 'Select All'}
          </button>
        ) : (
          <span />
        )}
        <span>{picked.size} selected</span>
      </div>
      <div
        className={clsx(
          'tw:max-h-64 tw:overflow-y-auto tw:rounded-lg tw:border',
          isEmpty ? 'tw:border-dashed tw:border-zinc-300' : 'tw:border-zinc-200'
        )}
      >
        {renderItems()}
      </div>
    </div>
  );
};
